import React from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { trackClickEmail, trackClickWhatsapp } from "../lib/tracking";
import { openCookieSettings } from "../lib/consent";
import { useActivePaymentBranding } from "../lib/settings";

const LOGO_WHITE = "/assets/logo-fped-white.svg";
// Netopia official trust strip — local SVG fork where the Netopia + Visa
// paths are rewritten to white so they read on the dark footer, while the
// Mastercard double-circle keeps its official red (#EB001B) / orange
// (#FF5F00) / yellow (#F79E1B) palette. No CSS filter applied — the SVG
// renders in true color.
const NETOPIA_LOGO = "/payment/netopia-trust-strip.svg";

// Format raw E.164 (e.g. "40722100023") into pretty "+40 722 100 023"
function formatPhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("40")) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return raw ? `+${digits}` : "";
}

export default function Footer() {
  const email = process.env.REACT_APP_CONTACT_EMAIL || "contact@fped.org";
  const rawPhone = process.env.REACT_APP_WHATSAPP_NUMBER || "40722100023";
  const phonePretty = formatPhone(rawPhone);
  const phoneTel = `+${rawPhone.replace(/\D/g, "")}`;
  // M901 — hide the Netopia trust strip when the active provider is anything
  // else (e.g. EuPlătesc). The neutral fallback simply omits the strip — we
  // do NOT render a third-party logo we don't own assets for.
  const branding = useActivePaymentBranding();
  return (
    <footer className="bg-[#373A45] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img
            src={LOGO_WHITE}
            alt="Fundația pentru Educație Digitală"
            className="h-16 w-auto object-contain"
          />
          <p className="mt-5 text-white/75 max-w-sm leading-relaxed text-sm">
            Aducem AI-ul în clasele din România într-un mod etic, practic și aplicabil chiar de luni dimineața.
          </p>
          <div className="mt-5 text-sm text-white/60 leading-relaxed">
            Sediu: București, Sectorul 1<br />
            Str. Ioan Bianu, Nr. 27<br />
            CIF: 43506207
          </div>
        </div>
        <div>
          <div className="text-white/60 text-xs uppercase tracking-widest font-semibold">Curs</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href="/#benefits" className="hover:text-[#965EA7]">Beneficii</a></li>
            <li><a href="/#curriculum" className="hover:text-[#965EA7]">Programa</a></li>
            <li><a href="/#pricing" className="hover:text-[#965EA7]">Preț</a></li>
            <li><a href="/#faq" className="hover:text-[#965EA7]">Întrebări</a></li>
          </ul>
          {/* ANPC SAL + SOL — discrete, transparent PNGs, link out */}
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="footer-anpc">
            <a
              href="https://anpc.ro/ce-este-sal/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ANPC – Soluționarea alternativă a litigiilor (SAL)"
              className="block opacity-80 hover:opacity-100 transition-opacity"
              data-testid="footer-anpc-sal"
            >
              <img
                src="/anpc/anpc-sal.png"
                alt="ANPC – Soluționarea Alternativă a Litigiilor"
                className="h-12 sm:h-14 w-auto"
                width="160"
                height="56"
                loading="lazy"
              />
            </a>
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ANPC – Soluționarea online a litigiilor (SOL / ODR)"
              className="block opacity-80 hover:opacity-100 transition-opacity"
              data-testid="footer-anpc-sol"
            >
              <img
                src="/anpc/anpc-sol.png"
                alt="ANPC – Soluționarea Online a Litigiilor (ODR)"
                className="h-12 sm:h-14 w-auto"
                width="120"
                height="56"
                loading="lazy"
              />
            </a>
          </div>
        </div>
        <div>
          <div className="text-white/60 text-xs uppercase tracking-widest font-semibold">Legal & Contact</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <a href={`mailto:${email}`} onClick={trackClickEmail} className="hover:text-[#965EA7]" data-testid="footer-email">
                {email}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${rawPhone.replace(/\D/g, "")}`}
                target="_blank" rel="noreferrer" onClick={trackClickWhatsapp}
                className="hover:text-[#965EA7]"
                data-testid="footer-whatsapp"
              >
                WhatsApp {phonePretty}
              </a>
            </li>
            <li>
              <a
                href={`tel:${phoneTel}`}
                className="hover:text-[#965EA7]"
                data-testid="footer-phone"
              >
                Telefon: {phonePretty}
              </a>
            </li>
            <li><Link to="/politica-confidentialitate" className="hover:text-[#965EA7]" data-testid="footer-privacy">Politica de confidențialitate</Link></li>
            <li><Link to="/politica-cookies" className="hover:text-[#965EA7]" data-testid="footer-cookies">Politica de cookies</Link></li>
            <li><Link to="/termeni-conditii" className="hover:text-[#965EA7]" data-testid="footer-terms">Termeni și condiții</Link></li>
            <li><Link to="/regulament-black-friday" className="hover:text-[#965EA7]" data-testid="footer-black-friday">Regulament campanie Black Friday</Link></li>
            <li>
              <button
                type="button"
                onClick={openCookieSettings}
                className="hover:text-[#965EA7] text-left"
                data-testid="footer-cookie-settings"
              >
                Setări cookies
              </button>
            </li>
            <li><Link to="/admin/login" className="hover:text-[#965EA7]" data-testid="footer-admin-link">Admin</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        {/* M901 — Provider-aware trust strip. Renders the official Netopia
            row ONLY when Netopia is the active provider. For any other
            provider (e.g. EuPlătesc), we render NOTHING here — the
            checkout page already carries the secure-payment trust badge
            close to the submit button, so a global footer row would be
            redundant and visually awkward. */}
        {branding.showNetopiaTrustStrip && (
          <div
            className="max-w-7xl mx-auto px-6 lg:px-8 pt-5 flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3 text-[11px] text-white/55"
            data-testid="footer-netopia-banner"
            data-analytics="footer-netopia"
          >
            <span className="inline-flex items-center gap-1.5">
              <Lock size={11} className="text-white/60" aria-hidden="true" />
              Plăți online securizate prin
            </span>
            <a
              href="https://netopia-payments.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Netopia Payments – procesator oficial de plăți online securizate"
              className="inline-flex items-center opacity-75 hover:opacity-100 transition-opacity"
            >
              <img
                src={NETOPIA_LOGO}
                alt="Netopia Payments – plăți online securizate"
                width="156"
                height="28"
                loading="lazy"
                decoding="async"
                className="h-7 sm:h-8 w-auto"
              />
            </a>
          </div>
        )}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 text-xs text-white/50 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>© {new Date().getFullYear()} Fundația pentru Educație Digitală. Toate drepturile rezervate.</div>
          <div>
            <a href="https://fped.org" target="_blank" rel="noopener noreferrer" className="hover:text-white">
              Despre Fundație → fped.org
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
